import { motion } from "framer-motion";
import { Shield, Lock, FileText, Mail } from "lucide-react";
import { PageHeader } from "@/components/layout/Navigation";

const sections = [
  {
    id: "introduction",
    title: "1. Introduction",
    body: [
      "NeoBody Inc. (\"NeoBody,\" \"we,\" \"us,\" or \"our\") provides client management software for healthcare organizations. We respect the privacy of our customers, the patients they serve, and visitors to our website. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services (collectively, the \"Services\").",
      "By accessing or using the Services, you agree to the practices described in this Privacy Policy. If you do not agree, please do not use the Services."
    ]
  },
  {
    id: "scope",
    title: "2. Scope and Roles",
    body: [
      "When NeoBody handles Protected Health Information (\"PHI\") on behalf of a healthcare organization (a \"Covered Entity\" under HIPAA), NeoBody acts as a Business Associate. Our use and disclosure of PHI is governed by the Business Associate Agreement (\"BAA\") executed with that Covered Entity, and by the Health Insurance Portability and Accountability Act (\"HIPAA\").",
      "When you visit our marketing website, request a demo, or otherwise interact with us in a non-PHI context, NeoBody acts as a data controller for the personal information you provide."
    ]
  },
  {
    id: "information-we-collect",
    title: "3. Information We Collect",
    body: [
      "Information You Provide: Name, business email, phone number, company, role, and any details you submit when requesting a demo, contacting sales, subscribing to communications, or creating an account.",
      "Customer Data: Information that our customers (or their authorized users) upload, enter, or transmit through the Services, including patient records, scheduling data, billing information, and clinical documentation. This data is processed under the customer's direction and our BAA.",
      "Automatically Collected Information: IP address, browser type, device identifiers, pages viewed, referring URLs, and timestamps. We use cookies, pixels, and similar technologies to understand usage and improve our Services.",
      "Information from Third Parties: We may receive information from integrated services (such as EHRs, calendars, or payment processors) and from public sources or business partners."
    ]
  },
  {
    id: "how-we-use",
    title: "4. How We Use Information",
    body: [
      "Provide, maintain, secure, and improve the Services.",
      "Authenticate users, prevent fraud, and enforce our Terms of Service.",
      "Respond to demo requests, support inquiries, and customer communications.",
      "Send transactional notices and—where permitted—marketing communications you can opt out of at any time.",
      "Generate de-identified or aggregated analytics that do not identify any individual.",
      "Comply with legal obligations and respond to lawful requests."
    ]
  },
  {
    id: "sharing",
    title: "5. How We Share Information",
    body: [
      "Service Providers: Vetted vendors that host infrastructure, send email, process payments, or provide analytics, all bound by contractual confidentiality and security obligations.",
      "Customers: We share Customer Data with the customer that owns it, and with users authorized by that customer.",
      "Legal & Safety: When required by law, subpoena, or court order, or to protect the rights, property, or safety of NeoBody, our customers, or others.",
      "Business Transfers: In connection with a merger, acquisition, financing, or sale of assets, subject to standard confidentiality protections.",
      "We do not sell personal information, and we do not share PHI for marketing purposes."
    ]
  },
  {
    id: "security",
    title: "6. Data Security",
    body: [
      "NeoBody maintains administrative, physical, and technical safeguards designed to protect the confidentiality, integrity, and availability of information we process. These include encryption in transit and at rest, role-based access controls, audit logging, vulnerability management, employee security training, and an incident response program aligned with the HIPAA Security Rule.",
      "No method of transmission or storage is 100% secure. If you believe your account has been compromised, contact us immediately at security@neobody.com."
    ]
  },
  {
    id: "retention",
    title: "7. Data Retention",
    body: [
      "We retain personal information for as long as necessary to provide the Services, comply with our legal obligations, resolve disputes, and enforce our agreements. Customer Data is retained per the customer's instructions and our BAA. When data is no longer needed, we delete or de-identify it using industry-standard procedures."
    ]
  },
  {
    id: "rights",
    title: "8. Your Rights and Choices",
    body: [
      "Depending on your jurisdiction, you may have the right to access, correct, delete, restrict, or port your personal information, and to object to certain processing. To exercise these rights, contact us at privacy@neobody.com. We will verify your identity before fulfilling requests.",
      "California residents have additional rights under the CCPA/CPRA, including the right to know what personal information we collect and to opt out of \"sharing\" as defined under that law. We do not sell personal information.",
      "Patients of our customers should direct privacy requests to the relevant healthcare organization, which acts as the controller of that data."
    ]
  },
  {
    id: "cookies",
    title: "9. Cookies and Tracking",
    body: [
      "We use first- and limited third-party cookies to operate the site, remember preferences, measure performance, and improve the Services. You can control cookies through your browser settings; disabling cookies may affect functionality. We honor Global Privacy Control (GPC) signals where required by law."
    ]
  },
  {
    id: "international",
    title: "10. International Transfers",
    body: [
      "NeoBody is headquartered in the United States, and information is processed primarily in U.S.-based data centers. If you access the Services from outside the U.S., your information will be transferred to and processed in the United States under appropriate safeguards."
    ]
  },
  {
    id: "children",
    title: "11. Children's Privacy",
    body: [
      "The Services are intended for use by healthcare organizations and their authorized personnel. We do not knowingly collect personal information directly from children under 13 through our marketing channels. PHI of minors that is processed through the Services is handled per the BAA with the relevant Covered Entity."
    ]
  },
  {
    id: "changes",
    title: "12. Changes to This Policy",
    body: [
      "We may update this Privacy Policy from time to time. Material changes will be posted on this page with a revised \"Last updated\" date and, where appropriate, communicated to customers through the Services or by email."
    ]
  },
  {
    id: "contact",
    title: "13. Contact Us",
    body: [
      "Questions about this Privacy Policy? We're here to help.",
      "NeoBody Inc. — Privacy Office",
      "Email: privacy@neobody.com",
      "Phone: (347) 342-8822"
    ]
  }
];

const PrivacyPage = () => {
  return (
    <div data-testid="privacy-page">
      <PageHeader
        title="Privacy Policy"
        subtitle="How NeoBody collects, uses, and protects your information."
        breadcrumbs={[{ name: "Privacy Policy" }]}
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          {/* Intro card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
          >
            <div className="bg-[#F9F9F7] rounded-2xl p-6 border border-[#1B4D3E]/10" data-testid="privacy-card-hipaa">
              <div className="w-12 h-12 rounded-xl bg-[#1B4D3E]/5 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl text-[#1A1A1A] mb-2">HIPAA Compliant</h3>
              <p className="text-sm text-[#4A4A4A] leading-relaxed">
                We act as a Business Associate and execute BAAs with every covered entity customer.
              </p>
            </div>
            <div className="bg-[#F9F9F7] rounded-2xl p-6 border border-[#1B4D3E]/10" data-testid="privacy-card-encryption">
              <div className="w-12 h-12 rounded-xl bg-[#1B4D3E]/5 flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl text-[#1A1A1A] mb-2">Encrypted Always</h3>
              <p className="text-sm text-[#4A4A4A] leading-relaxed">
                All data is encrypted in transit (TLS 1.2+) and at rest (AES-256) by default.
              </p>
            </div>
            <div className="bg-[#F9F9F7] rounded-2xl p-6 border border-[#1B4D3E]/10" data-testid="privacy-card-no-sale">
              <div className="w-12 h-12 rounded-xl bg-[#1B4D3E]/5 flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl text-[#1A1A1A] mb-2">No Data Sales</h3>
              <p className="text-sm text-[#4A4A4A] leading-relaxed">
                We never sell personal information or PHI. Period.
              </p>
            </div>
          </motion.div>

          {/* Last updated */}
          <p className="text-sm text-[#888] mb-12" data-testid="privacy-last-updated">
            Last updated: February 1, 2026
          </p>

          {/* Table of contents */}
          <div className="bg-[#F9F9F7] rounded-2xl p-8 mb-16 border border-[#1B4D3E]/10">
            <h3 className="font-display text-xl text-[#1A1A1A] mb-4">Contents</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-[#1B4D3E] hover:text-[#D4AF37] transition-colors text-sm">
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Sections */}
          <div className="space-y-14">
            {sections.map((s, idx) => (
              <motion.div
                key={s.id}
                id={s.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: idx * 0.02 }}
                data-testid={`privacy-section-${s.id}`}
              >
                <h2 className="font-display text-2xl md:text-3xl text-[#1A1A1A] mb-4 scroll-mt-32">
                  {s.title}
                </h2>
                <div className="space-y-4">
                  {s.body.map((p, i) => (
                    <p key={i} className="text-[#4A4A4A] font-light leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-20 bg-[#1B4D3E] rounded-3xl p-10 md:p-14 text-center">
            <Mail className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-3xl text-white mb-3">Questions about your privacy?</h3>
            <p className="text-white/70 font-light mb-6 max-w-xl mx-auto">
              Our Privacy Office is happy to help with any questions or data subject requests.
            </p>
            <a
              href="mailto:privacy@neobody.com"
              className="btn-gold inline-flex"
              data-testid="privacy-contact-email"
            >
              privacy@neobody.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
